import clsx from 'clsx';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ORDER_FLOW } from '../../components/ui/status';
import { formatApprox } from './format';
import type { TimelineRow } from './timeline';

const labelOf = (row: TimelineRow, t: (key: string, o?: Record<string, unknown>) => string): string => {
    switch (row.kind) {
        case 'created':
            return t('orderDetail:timeline.created');
        case 'status': {
            const status = t(`common:status.${row.to}`);
            if (row.to === 'cancelled') return t('orderDetail:timeline.cancelled');
            if (row.from === 'cancelled') return t('orderDetail:timeline.reopened', { status });
            const back = row.from !== null && ORDER_FLOW.indexOf(row.to) < ORDER_FLOW.indexOf(row.from);
            return back ? t('orderDetail:timeline.back', { status }) : status;
        }
        case 'paid':
            return row.method
                ? t('orderDetail:timeline.paidWith', { method: t(`common:payment.${row.method}`) })
                : t('orderDetail:timeline.paid');
        case 'unpaid':
            return t('orderDetail:timeline.unpaid');
        case 'method':
            return t('orderDetail:timeline.method', { method: t(`common:payment.${row.method}`) });
        case 'note':
            return t(row.from === null ? 'orderDetail:timeline.noteAdded' : row.to === null ? 'orderDetail:timeline.noteRemoved' : 'orderDetail:timeline.noteEdited');
    }
};

/** Every change to the order, oldest first, as an ordered list. */
export const OrderTimeline = ({ rows }: { rows: readonly TimelineRow[] }) => {
    const { t } = useTranslation(['orderDetail', 'common']);
    return (
        <ol aria-label={t('orderDetail:timeline.title')}>
            {rows.map((row, i) => {
                const at = dayjs(row.at);
                const actor = row.actor.type === 'staff' ? (row.actor.name ?? t('orderDetail:actor.staff')) : t(`orderDetail:actor.${row.actor.type}`);
                const cancelled = row.kind === 'status' && row.to === 'cancelled';
                return (
                    <li
                        key={row.id}
                        className="relative pb-4 ps-6 before:absolute before:bottom-0 before:start-[5px] before:top-3 before:w-px before:bg-line last:pb-0 last:before:hidden"
                    >
                        <span
                            aria-hidden="true"
                            className={clsx(
                                'absolute start-0 top-1.5 size-3 rounded-full',
                                cancelled ? 'bg-danger-solid' : i === rows.length - 1 ? 'bg-accent' : 'bg-control'
                            )}
                        />
                        <p className="break-words text-small font-medium text-strong">{labelOf(row, t)}</p>
                        {row.kind === 'note' && row.to && <p className="whitespace-pre-wrap break-words text-small text-body">{row.to}</p>}
                        <p className="text-caption text-muted">
                            <time dateTime={row.at} className="tabular-nums">
                                {at.format(at.isSame(dayjs(), 'day') ? 'HH:mm:ss' : 'D MMM, HH:mm:ss')}
                            </time>
                            {' · '}
                            {actor}
                        </p>
                        {row.spent && (
                            <p className="text-caption text-muted">
                                {t('orderDetail:timeline.spent', {
                                    time: formatApprox(row.spent.seconds, row.estimated, t),
                                    status: t(`common:status.${row.spent.status}`),
                                })}
                            </p>
                        )}
                    </li>
                );
            })}
        </ol>
    );
};
